import { useMutation } from '@tanstack/react-query'
import { getApi } from '@BUP/services/Axios'

/**
* Generic hook for mutation requests with React Query
 * @param {string} service - service name for getApi
 * @param {string|Function} endpoint - endpoint or function that resolves it with variables
 * @param {string} method - HTTP method (post, put, patch, delete)
 * @param {object} options - extra react-query options (onSuccess, onError, etc.)
 * @param {object} configOverride - optional extra configuration for getApi
 */
export const useApiMutation = (
  service,
  endpoint,
  method = 'post',
  options = {},
  configOverride
) => {
  const { meta: optionsMeta, ...restOptions } = options

  return useMutation({
    mutationFn: async (variables = {}) => {
      const { data, params, method: methodOverride } = variables
      const methodToUse = (methodOverride || method).toLowerCase()
      const resolvedEndpoint =
        typeof endpoint === 'function' ? endpoint(variables) : endpoint

      const defaultConfig =
        service === 'schools'
          ? { authenticated: true, version: 3 }
          : { authenticated: true }

      const client = await getApi(service, configOverride || defaultConfig)

      let response
      if (methodToUse === 'delete') {
        response = await client.delete(resolvedEndpoint, { data, params })
      } else {
        response = await client[methodToUse](
          resolvedEndpoint,
          data,
          params ? { params } : undefined
        )
      }

      if (response.status === 204) return []
      if (response.status >= 200 && response.status < 300) {
        if (typeof response.data !== 'object') {
          throw new Error(
            `The API did not return JSON. Type received: ${typeof response.data}`
          )
        }
        return response.data ?? []
      }

      throw new Error(`Request failed with status ${response.status}`)
    },
    meta: {
      service,
      endpoint: typeof endpoint === 'string' ? endpoint : undefined,
      ...optionsMeta
    },
    ...restOptions
  })
}
